import { useEffect, useRef } from "react";
import { RemotionPreview } from "./RemotionPreview";

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  moduleUrl: string | null;
  inputProps: Record<string, unknown>;
  durationFrames: number;
  fps: number;
  width: number;
  height: number;
  contentScale?: number;
  baseWidth?: number;
  baseHeight?: number;
}

/**
 * Fullscreen preview modal. Renders the RemotionPreview on a dark backdrop.
 * Closes on Escape or click outside the preview content.
 */
export function PreviewModal({
  open,
  onClose,
  moduleUrl,
  inputProps,
  durationFrames,
  fps,
  width,
  height,
  contentScale,
  baseWidth,
  baseHeight,
}: PreviewModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.95)" }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-surface-2 text-lg text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
      >
        ×
      </button>
      <div className="absolute left-4 top-4 text-xs text-ink-3">
        Press Esc or click outside to close
      </div>
      <div
        ref={contentRef}
        className="flex max-h-[92vh] max-w-[92vw] items-center justify-center"
      >
        <div className="w-[92vw]">
          <RemotionPreview
            moduleUrl={moduleUrl}
            inputProps={inputProps}
            durationFrames={durationFrames}
            fps={fps}
            width={width}
            height={height}
            contentScale={contentScale}
            baseWidth={baseWidth}
            baseHeight={baseHeight}
          />
        </div>
      </div>
    </div>
  );
}
